(function () {
  "use strict";

  /**
   * FedEx insurance module
   * ----------------------
   * Config lives in the constants below.
   *
   * Quick setup:
   * 1) Set INSURANCE_VARIANT_ID to the hidden insurance product variant ID.
   * 2) Keep variant price at $1.65 in Shopify admin.
   * 3) Set AUTO_ENABLE_OVER_SUBTOTAL (dollars) if you want automatic enable.
   *
   * Math model:
   * - Merchandise subtotal = sum(final_line_price) of all NON-insurance items.
   * - Units (default) = ceil(subtotal / $100).
   * - If SUBTRACT_INCLUDED_COVERAGE = true:
   *   units = max(0, ceil((subtotal - INCLUDED_COVERAGE_AMOUNT) / $100)).
   * - Declared value fee = units * $1.65.
   *
   * Testing checklist:
   * - Toggle on/off from cart page and cart drawer.
   * - Change item quantities rapidly and confirm insurance quantity re-syncs.
   * - Confirm no duplicate insurance lines are created.
   * - Confirm persisted attribute CART_ATTRIBUTE_ENABLED_KEY is honored on reload.
   * - Confirm insurance is removed when merchandise subtotal goes to zero.
   *
   * Ops note:
   * - The insurance line item quantity maps to declared coverage units.
   * - 1 quantity unit = $100 coverage.
   * - During FedEx label creation, fulfillment should set declared value to the intended
   *   covered merchandise value for the shipment.
   */
  const INSURANCE_VARIANT_ID = 48842879467772;
  const INSURANCE_PRICE_PER_UNIT = 1.65;
  const INSURANCE_UNIT_COVERAGE = 100;
  const MAX_DECLARED_VALUE = 50000;
  const MAX_UNITS = Math.ceil(MAX_DECLARED_VALUE / INSURANCE_UNIT_COVERAGE);
  const SUBTRACT_INCLUDED_COVERAGE = false;
  const INCLUDED_COVERAGE_AMOUNT = 100;
  const AUTO_ENABLE_OVER_SUBTOTAL = 0;
  const CART_ATTRIBUTE_ENABLED_KEY = "fedex_insurance_enabled";
  const DECLARED_VALUE_INFO_URL = "https://www.fedex.com/en-us/shipping/declared-value.html";
  const DEBUG_INSURANCE = false;

  const SELECTORS = {
    mount: ".js-fedex-insurance-mount",
    toggle: ".js-fedex-insurance-toggle",
    summary: ".js-fedex-insurance-summary",
    cartFooter: "#main-cart-footer",
    cartDrawer: "#cart-notification",
    cartItems: "#main-cart-items",
    cartDrawerItems: "#cart-notification-product",
  };

  const ENDPOINTS = {
    cart: "/cart.js",
    add: (window.routes && window.routes.cart_add_url) || "/cart/add.js",
    change: (window.routes && window.routes.cart_change_url) || "/cart/change.js",
    update: (window.routes && window.routes.cart_update_url) || "/cart/update.js",
  };

  const STATE = {
    initialized: false,
    syncInProgress: false,
    needsResync: false,
    syncDebounceTimer: null,
    reinitDebounceTimer: null,
    lastCart: null,
    unsubscribeCartUpdate: null,
    observer: null,
    fetchPatched: false,
    internalRequestDepth: 0,
  };

  function debugLog() {
    if (!DEBUG_INSURANCE) return;
    const args = Array.prototype.slice.call(arguments);
    args.unshift("[FedExInsurance]");
    console.log.apply(console, args);
  }

  function parsePreferenceValue(value) {
    if (typeof value !== "string") return null;
    const normalized = value.trim().toLowerCase();
    if (["true", "1", "yes", "on"].indexOf(normalized) > -1) return true;
    if (["false", "0", "no", "off"].indexOf(normalized) > -1) return false;
    return null;
  }

  function isInsuranceItem(item) {
    return Number(item && item.variant_id) === Number(INSURANCE_VARIANT_ID);
  }

  function findInsuranceItem(cart) {
    if (!cart || !Array.isArray(cart.items)) return null;
    return cart.items.find(isInsuranceItem) || null;
  }

  function findInsuranceItems(cart) {
    if (!cart || !Array.isArray(cart.items)) return [];
    return cart.items.filter(isInsuranceItem);
  }

  function toCents(dollars) {
    return Math.round(Number(dollars || 0) * 100);
  }

  function calculateMerchandiseSubtotal(cart) {
    if (!cart || !Array.isArray(cart.items)) return 0;
    return cart.items.reduce(function (sum, item) {
      if (isInsuranceItem(item)) return sum;
      return sum + Number(item.final_line_price || 0);
    }, 0);
  }

  function calculateInsuranceUnits(subtotalCents) {
    const unitCoverageCents = toCents(INSURANCE_UNIT_COVERAGE);

    if (subtotalCents <= 0 || unitCoverageCents <= 0) return 0;

    if (!SUBTRACT_INCLUDED_COVERAGE) {
      return Math.min(MAX_UNITS, Math.ceil(subtotalCents / unitCoverageCents));
    }

    const includedCoverageCents = toCents(INCLUDED_COVERAGE_AMOUNT);
    const remaining = subtotalCents - includedCoverageCents;
    if (remaining <= 0) return 0;
    return Math.min(MAX_UNITS, Math.ceil(remaining / unitCoverageCents));
  }

  function calculateInsuranceChargeCents(units) {
    return Math.round(Number(units || 0) * INSURANCE_PRICE_PER_UNIT * 100);
  }

  function shouldAutoEnable(subtotalCents, hasPersistedPreference) {
    if (hasPersistedPreference) return false;
    if (AUTO_ENABLE_OVER_SUBTOTAL <= 0) return false;
    return subtotalCents > toCents(AUTO_ENABLE_OVER_SUBTOTAL);
  }

  function getInsurancePreference(cart, subtotalCents) {
    const raw = cart && cart.attributes ? cart.attributes[CART_ATTRIBUTE_ENABLED_KEY] : null;
    const parsed = parsePreferenceValue(raw);
    if (parsed !== null) return parsed;
    return shouldAutoEnable(subtotalCents, false);
  }

  function getCartCurrency(cart) {
    if (cart && cart.currency) return cart.currency;
    if (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) {
      return window.Shopify.currency.active;
    }
    return "USD";
  }

  function formatMoneyFromCents(cents, currency) {
    try {
      return new Intl.NumberFormat(undefined, {
        style: "currency",
        currency: currency || "USD",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format((Number(cents || 0) / 100));
    } catch (error) {
      return "$" + ((Number(cents || 0) / 100).toFixed(2));
    }
  }

  function getFetchUrl(input) {
    if (typeof input === "string") return input;
    if (input && typeof input.url === "string") return input.url;
    return "";
  }

  function getFetchMethod(input, init) {
    if (init && init.method) return String(init.method).toUpperCase();
    if (input && input.method) return String(input.method).toUpperCase();
    return "GET";
  }

  function isCartMutationRequest(input, init) {
    const rawUrl = getFetchUrl(input);
    const method = getFetchMethod(input, init);
    if (!rawUrl || method !== "POST") return false;

    const url = new URL(rawUrl, window.location.origin);
    const path = url.pathname;

    return path === "/cart/add.js" || path === "/cart/change.js" || path === "/cart/update.js" || path === "/cart/clear.js";
  }

  function patchFetchForCartChanges() {
    if (STATE.fetchPatched || typeof window.fetch !== "function") return;

    const originalFetch = window.fetch.bind(window);
    window.fetch = function (input, init) {
      const promise = originalFetch(input, init);

      if (!isCartMutationRequest(input, init)) {
        return promise;
      }

      if (STATE.internalRequestDepth > 0) {
        return promise;
      }

      promise
        .then(function () {
          scheduleSync(120);
        })
        .catch(function () {
          scheduleSync(220);
        });

      return promise;
    };

    STATE.fetchPatched = true;
  }

  async function requestCart(url, options) {
    STATE.internalRequestDepth += 1;
    try {
      const response = await window.fetch(url, options || {});
      if (!response.ok) {
        throw new Error("Cart request failed: " + response.status + " " + response.statusText);
      }
      return await response.json();
    } finally {
      STATE.internalRequestDepth = Math.max(0, STATE.internalRequestDepth - 1);
    }
  }

  function getCart() {
    return requestCart(ENDPOINTS.cart, {
      credentials: "same-origin",
      headers: {
        Accept: "application/json",
      },
    });
  }

  function updateCartAttributes(attributes) {
    const body = JSON.stringify({ attributes: attributes });
    return requestCart(ENDPOINTS.update, {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: body,
    });
  }

  function setInsuranceQuantityByVariant(units) {
    const updates = {};
    updates[String(INSURANCE_VARIANT_ID)] = Number(units || 0);

    const body = JSON.stringify({ updates: updates });
    return requestCart(ENDPOINTS.update, {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: body,
    });
  }

  function addInsurance(units) {
    const body = JSON.stringify({
      id: INSURANCE_VARIANT_ID,
      quantity: Number(units || 0),
    });

    debugLog("addInsurance", units);
    return requestCart(ENDPOINTS.add, {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: body,
    }).catch(function (error) {
      debugLog("addInsurance fallback to cart/update.js", error && error.message);
      return setInsuranceQuantityByVariant(units);
    });
  }

  function updateInsurance(lineKey, units) {
    const body = JSON.stringify({ id: lineKey, quantity: units });

    debugLog("updateInsurance", lineKey, units);

    return requestCart(ENDPOINTS.change, {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: body,
    }).catch(function (error) {
      debugLog("updateInsurance fallback to cart/update.js", error && error.message);
      return setInsuranceQuantityByVariant(units);
    });
  }

  function removeInsurance(lineKey) {
    const body = JSON.stringify({ id: lineKey, quantity: 0 });

    debugLog("removeInsurance", lineKey);

    return requestCart(ENDPOINTS.change, {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: body,
    }).catch(function (error) {
      debugLog("removeInsurance fallback to cart/update.js", error && error.message);
      return setInsuranceQuantityByVariant(0);
    });
  }

  function publishInsuranceUpdated(cart) {
    document.dispatchEvent(
      new CustomEvent("fedex-insurance:updated", {
        detail: {
          itemCount: cart ? cart.item_count : 0,
          subtotalCents: calculateMerchandiseSubtotal(cart || { items: [] }),
        },
      })
    );
  }

  async function refreshCartPageSections() {
    const cartItemsRoot = document.querySelector("cart-items");
    const mainCartItems = document.getElementById("main-cart-items");
    const mainCartFooter = document.getElementById("main-cart-footer");

    if (!cartItemsRoot || !mainCartItems || !mainCartFooter) return;

    const itemsSectionId = mainCartItems.dataset.id;
    const footerSectionId = mainCartFooter.dataset.id;

    if (itemsSectionId) {
      const response = await window.fetch(
        window.location.pathname + "?section_id=" + encodeURIComponent(itemsSectionId),
        { credentials: "same-origin" }
      );
      if (response.ok) {
        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, "text/html");
        const sourceCartItems = doc.querySelector("cart-items");
        if (sourceCartItems) {
          cartItemsRoot.innerHTML = sourceCartItems.innerHTML;
        }
      }
    }

    if (footerSectionId) {
      const response = await window.fetch(
        window.location.pathname + "?section_id=" + encodeURIComponent(footerSectionId),
        { credentials: "same-origin" }
      );
      if (response.ok) {
        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, "text/html");
        const sourceFooter = doc.getElementById("main-cart-footer");
        const currentFooterContents = mainCartFooter.querySelector(".js-contents");
        const sourceFooterContents = sourceFooter && sourceFooter.querySelector(".js-contents");
        if (currentFooterContents && sourceFooterContents) {
          currentFooterContents.innerHTML = sourceFooterContents.innerHTML;
        }
      }
    }
  }

  async function refreshCartDrawerSections() {
    if (!document.getElementById("cart-notification")) return;

    const sections = [
      { sectionId: "cart-notification-product", targetId: "cart-notification-product" },
      { sectionId: "cart-notification-subtotal", targetId: "cart-notification-subtotal" },
      { sectionId: "cart-notification-count", targetId: "cart-notification-count" },
      { sectionId: "cart-notification-discount", targetId: "cart-notification-discount" },
    ];

    await Promise.all(
      sections.map(async function (section) {
        const target = document.getElementById(section.targetId);
        if (!target) return;

        const response = await window.fetch(
          window.location.pathname + "?section_id=" + encodeURIComponent(section.sectionId),
          { credentials: "same-origin" }
        );
        if (!response.ok) return;

        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, "text/html");
        const source = doc.querySelector(".shopify-section");
        if (source) {
          target.innerHTML = source.innerHTML;
        }
      })
    );
  }

  async function refreshThemeCartUI() {
    try {
      await Promise.all([refreshCartPageSections(), refreshCartDrawerSections()]);
    } catch (error) {
      debugLog("refreshThemeCartUI failed", error && error.message);
    }
  }

  function getDisplayState(cart) {
    const subtotalCents = calculateMerchandiseSubtotal(cart);
    const hasMerchandise = subtotalCents > 0;
    const enabled = hasMerchandise && getInsurancePreference(cart, subtotalCents);
    const units = enabled ? calculateInsuranceUnits(subtotalCents) : 0;
    const maxDeclaredValueCents = toCents(MAX_DECLARED_VALUE);
    const declaredValueCents = enabled ? Math.min(subtotalCents, maxDeclaredValueCents) : 0;
    const insuranceChargeCents = enabled ? calculateInsuranceChargeCents(units) : 0;
    const currency = getCartCurrency(cart);
    const isCapped = enabled && subtotalCents > maxDeclaredValueCents;

    return {
      hasMerchandise: hasMerchandise,
      enabled: Boolean(enabled),
      subtotalCents: subtotalCents,
      units: units,
      declaredValueCents: declaredValueCents,
      insuranceChargeCents: insuranceChargeCents,
      currency: currency,
      isCapped: isCapped,
      maxDeclaredValueCents: maxDeclaredValueCents,
    };
  }

  function getMounts() {
    return Array.prototype.slice.call(document.querySelectorAll(SELECTORS.mount));
  }

  function renderInsuranceUI(container, state) {
    const declaredValueText = formatMoneyFromCents(state.declaredValueCents, state.currency);
    const maxDeclaredValueText = formatMoneyFromCents(state.maxDeclaredValueCents, state.currency);
    const chargeText = formatMoneyFromCents(state.insuranceChargeCents, state.currency);

    container.innerHTML =
      '<div class="fedex-insurance" data-fedex-insurance-widget="true">' +
      '  <label class="fedex-insurance__header">' +
      '    <input type="checkbox" class="fedex-insurance__toggle js-fedex-insurance-toggle"' +
      (state.enabled ? " checked" : "") +
      (state.hasMerchandise ? "" : " disabled") +
      '>' +
      '    <span class="fedex-insurance__title-wrap">' +
      '      <span class="fedex-insurance__title">Increase FedEx Declared Value (Not Insurance)</span>' +
      '      <span class="fedex-insurance__subtitle">FedEx Declared Value Increase ($100 per unit)</span>' +
      '    </span>' +
      "  </label>" +
      '  <p class="fedex-insurance__description">Declared value is calculated at $1.65 per $100 of merchandise value. This is not shipping insurance.* <a href="' +
      DECLARED_VALUE_INFO_URL +
      '" target="_blank" rel="noopener noreferrer">*FedEx Declared Value Page</a>.</p>' +
      (state.enabled
        ? '  <div class="fedex-insurance__summary js-fedex-insurance-summary">' +
          '    <div class="fedex-insurance__summary-row"><span>Declared value amount</span><strong>' +
          declaredValueText +
          '</strong></div>' +
          '    <div class="fedex-insurance__summary-row"><span>Units</span><strong>' +
          String(state.units) +
          '</strong></div>' +
          '    <div class="fedex-insurance__summary-row"><span>Declared value fee</span><strong>' +
          chargeText +
          '</strong></div>' +
          (state.isCapped
            ? '    <p class="fedex-insurance__helper">Maximum declared value through this checkout add-on is ' +
              maxDeclaredValueText +
              ".</p>"
            : "") +
          "  </div>"
        : "") +
      (state.hasMerchandise
        ? ""
        : '  <p class="fedex-insurance__helper">Add an item to cart to enable declared value.</p>') +
      "</div>";
  }

  function renderAll(cart) {
    const state = getDisplayState(cart);
    const mounts = getMounts();

    mounts.forEach(function (mount) {
      renderInsuranceUI(mount, state);
    });

    bindInsuranceUI();
  }

  function setToggleDisabled(disabled) {
    const toggles = document.querySelectorAll(SELECTORS.toggle);
    toggles.forEach(function (toggle) {
      toggle.disabled = Boolean(disabled);
    });
  }

  async function setInsurancePreference(enabled) {
    const value = enabled ? "true" : "false";
    const attributes = {};
    attributes[CART_ATTRIBUTE_ENABLED_KEY] = value;
    await updateCartAttributes(attributes);
  }

  async function syncInsuranceOnce(source) {
    debugLog("syncInsuranceOnce", source);
    let didMutateCart = false;

    let cart = await getCart();
    let subtotalCents = calculateMerchandiseSubtotal(cart);
    let hasMerchandise = subtotalCents > 0;
    let enabled = hasMerchandise && getInsurancePreference(cart, subtotalCents);
    let targetUnits = enabled ? calculateInsuranceUnits(subtotalCents) : 0;
    let insuranceItems = findInsuranceItems(cart);
    let insuranceItem = insuranceItems[0] || null;

    if (insuranceItems.length > 1) {
      for (let i = 1; i < insuranceItems.length; i += 1) {
        await removeInsurance(insuranceItems[i].key);
        didMutateCart = true;
      }
      cart = await getCart();
      insuranceItems = findInsuranceItems(cart);
      insuranceItem = insuranceItems[0] || null;
      subtotalCents = calculateMerchandiseSubtotal(cart);
      hasMerchandise = subtotalCents > 0;
      enabled = hasMerchandise && getInsurancePreference(cart, subtotalCents);
      targetUnits = enabled ? calculateInsuranceUnits(subtotalCents) : 0;
    }

    if (!enabled && insuranceItem) {
      await removeInsurance(insuranceItem.key);
      didMutateCart = true;
      cart = await getCart();
      subtotalCents = calculateMerchandiseSubtotal(cart);
      hasMerchandise = subtotalCents > 0;
      insuranceItem = findInsuranceItem(cart);
    } else if (enabled && targetUnits > 0 && !insuranceItem) {
      await addInsurance(targetUnits);
      didMutateCart = true;
      cart = await getCart();
      subtotalCents = calculateMerchandiseSubtotal(cart);
      hasMerchandise = subtotalCents > 0;
      insuranceItem = findInsuranceItem(cart);
    } else if (enabled && insuranceItem && insuranceItem.quantity !== targetUnits) {
      await updateInsurance(insuranceItem.key, targetUnits);
      didMutateCart = true;
      cart = await getCart();
      subtotalCents = calculateMerchandiseSubtotal(cart);
      hasMerchandise = subtotalCents > 0;
      insuranceItem = findInsuranceItem(cart);
    }

    if (!hasMerchandise) {
      enabled = false;
      targetUnits = 0;

      if (insuranceItem) {
        await removeInsurance(insuranceItem.key);
        didMutateCart = true;
        cart = await getCart();
      }
    }

    const currentPref = parsePreferenceValue(
      cart && cart.attributes ? cart.attributes[CART_ATTRIBUTE_ENABLED_KEY] : null
    );
    if (currentPref !== Boolean(enabled)) {
      const attrs = {};
      attrs[CART_ATTRIBUTE_ENABLED_KEY] = enabled ? "true" : "false";
      await updateCartAttributes(attrs);
      didMutateCart = true;
      cart = await getCart();
    }

    const currentInsurance = findInsuranceItem(cart);
    if (!enabled && currentInsurance) {
      await removeInsurance(currentInsurance.key);
      didMutateCart = true;
      cart = await getCart();
    }

    STATE.lastCart = cart;
    if (didMutateCart) {
      await refreshThemeCartUI();
    }
    renderAll(cart);
    publishInsuranceUpdated(cart);
  }

  async function syncInsurance(source) {
    if (STATE.syncInProgress) {
      STATE.needsResync = true;
      return;
    }

    STATE.syncInProgress = true;
    setToggleDisabled(true);

    try {
      do {
        STATE.needsResync = false;
        await syncInsuranceOnce(source || "sync");
      } while (STATE.needsResync);
    } catch (error) {
      console.error("FedEx insurance sync failed", error);
      try {
        const fresh = await getCart();
        STATE.lastCart = fresh;
        renderAll(fresh);
      } catch (nestedError) {
        console.error("FedEx insurance fallback cart fetch failed", nestedError);
      }
    } finally {
      STATE.syncInProgress = false;
      setToggleDisabled(false);
    }
  }

  function scheduleSync(delayMs) {
    const delay = typeof delayMs === "number" ? delayMs : 180;

    if (STATE.syncDebounceTimer) {
      window.clearTimeout(STATE.syncDebounceTimer);
    }

    STATE.syncDebounceTimer = window.setTimeout(function () {
      syncInsurance("debounced");
    }, delay);
  }

  function bindInsuranceUI() {
    const toggles = document.querySelectorAll(SELECTORS.toggle);

    toggles.forEach(function (toggle) {
      if (toggle.dataset.fedexInsuranceBound === "true") return;

      toggle.dataset.fedexInsuranceBound = "true";
      toggle.addEventListener("change", async function (event) {
        const checked = Boolean(event.target.checked);

        setToggleDisabled(true);

        try {
          await setInsurancePreference(checked);
          await syncInsurance("user-toggle");
        } catch (error) {
          console.error("FedEx insurance toggle update failed", error);
          scheduleSync(0);
        } finally {
          setToggleDisabled(false);
        }
      });
    });
  }

  function subscribeToThemeCartEvents() {
    const subscribeFn = typeof subscribe === "function" ? subscribe : window.subscribe;
    const cartUpdateEventName =
      (typeof PUB_SUB_EVENTS !== "undefined" && PUB_SUB_EVENTS && PUB_SUB_EVENTS.cartUpdate) ||
      (window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate);

    if (typeof subscribeFn === "function" && cartUpdateEventName) {
      STATE.unsubscribeCartUpdate = subscribeFn(cartUpdateEventName, function (event) {
        if (event && event.source === "fedex-insurance") return;
        scheduleSync(120);
      });
    }

    document.addEventListener("fedex-insurance:resync", function () {
      scheduleSync(0);
    });
  }

  function scheduleReinit() {
    if (STATE.reinitDebounceTimer) {
      window.clearTimeout(STATE.reinitDebounceTimer);
    }

    STATE.reinitDebounceTimer = window.setTimeout(function () {
      if (STATE.lastCart) {
        renderAll(STATE.lastCart);
      } else {
        scheduleSync(80);
      }
    }, 80);
  }

  function setupMutationObserver() {
    if (STATE.observer || typeof MutationObserver === "undefined") return;

    STATE.observer = new MutationObserver(function (mutations) {
      let shouldReinit = false;

      for (let i = 0; i < mutations.length; i += 1) {
        const mutation = mutations[i];
        if (!mutation.addedNodes || mutation.addedNodes.length === 0) continue;

        for (let j = 0; j < mutation.addedNodes.length; j += 1) {
          const node = mutation.addedNodes[j];
          if (!node || node.nodeType !== 1) continue;

          if (
            node.matches &&
            (node.matches(SELECTORS.mount) ||
              node.matches(SELECTORS.cartFooter) ||
              node.matches(SELECTORS.cartDrawer) ||
              node.matches(SELECTORS.cartItems) ||
              node.matches(SELECTORS.cartDrawerItems))
          ) {
            shouldReinit = true;
            break;
          }

          if (
            node.querySelector &&
            (node.querySelector(SELECTORS.mount) ||
              node.querySelector(SELECTORS.cartFooter) ||
              node.querySelector(SELECTORS.cartDrawer) ||
              node.querySelector(SELECTORS.cartItems) ||
              node.querySelector(SELECTORS.cartDrawerItems))
          ) {
            shouldReinit = true;
            break;
          }
        }

        if (shouldReinit) break;
      }

      if (shouldReinit) {
        scheduleReinit();
      }
    });

    STATE.observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  function initInsurance() {
    if (STATE.initialized) return;
    STATE.initialized = true;

    if (!document.querySelector(SELECTORS.mount)) {
      debugLog("No insurance mount found yet; waiting for observer.");
    }

    patchFetchForCartChanges();
    subscribeToThemeCartEvents();
    setupMutationObserver();
    scheduleSync(0);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initInsurance);
  } else {
    initInsurance();
  }
})();
